import { useState } from 'react';
import {
  DOCUMENT_STATUS_LABELS,
  PDFA_STATUS_LABELS,
  PIPELINE_STATUS_LABELS,
  type DocumentDto,
  type PdfaStatus,
  type PdfaValidationDto,
  type PipelineStatus,
  type PipelineTransitionDto,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import type { ArtifactKind } from '../../lib/api';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useApi } from '../../lib/ApiProvider';
import { usePipelineStatus, useRetryPipeline, useStartPipeline } from './hooks';

/** Terminal pipeline states (mirror of the server's set) — a run in these is no longer active. */
const TERMINAL: ReadonlySet<PipelineStatus> = new Set<PipelineStatus>([
  'PDFA_VALID',
  'PDFA_VALIDATION_FAILED_FINAL',
  'FAILED',
]);

const FAILED: ReadonlySet<PipelineStatus> = new Set<PipelineStatus>([
  'FAILED',
  'PDFA_VALIDATION_FAILED_FINAL',
]);

function pdfaTone(status: PdfaStatus): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'VALID':
      return 'success';
    case 'VALIDATION_FAILED':
      return 'danger';
    case 'NOT_GENERATED':
      return 'neutral';
    default:
      return 'warning';
  }
}

function pipelineTone(status: PipelineStatus): 'neutral' | 'success' | 'warning' | 'danger' {
  if (status === 'PDFA_VALID') return 'success';
  if (FAILED.has(status)) return 'danger';
  return 'warning';
}

/** One step of the run's transition history. */
function TimelineStep({
  transition,
  current,
}: {
  transition: PipelineTransitionDto;
  current: boolean;
}): JSX.Element {
  return (
    <li className="flex items-center gap-2">
      <span
        aria-hidden
        className={
          current
            ? 'h-2.5 w-2.5 shrink-0 rounded-full bg-brand-600 ring-2 ring-brand-200'
            : 'h-2.5 w-2.5 shrink-0 rounded-full bg-slate-300'
        }
      />
      <span className={current ? 'text-sm font-semibold text-slate-900' : 'text-sm text-slate-600'}>
        {PIPELINE_STATUS_LABELS[transition.toStatus]}
      </span>
      {current ? <Badge tone="info">Étape actuelle</Badge> : null}
      <span className="ml-auto text-xs text-slate-400">
        {new Date(transition.createdAt).toLocaleString('fr-FR')}
      </span>
    </li>
  );
}

/** One veraPDF validation line — veraPDF is the SOLE conformance authority. */
function ValidationRow({ validation }: { validation: PdfaValidationDto }): JSX.Element {
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-3 text-sm">
      <Badge tone={validation.result ? 'success' : 'danger'}>
        {validation.result ? 'Valide' : 'Invalide'}
      </Badge>
      {validation.isRevalidation ? <Badge tone="warning">Revalidation</Badge> : null}
      <span className="text-slate-700">
        veraPDF {validation.veraPdfVersion} · profil {validation.profile}
      </span>
      <span className="text-xs text-slate-500">
        {validation.errorsCount} erreur(s) · {validation.warningsCount} avertissement(s)
      </span>
      {validation.ghostscriptUsed ? (
        <span className="text-xs text-amber-700">après Ghostscript</span>
      ) : null}
    </li>
  );
}

/**
 * « Chaîne PDF/A » — the LOT 7B archival pipeline view of a document. The SERVER is the sole authority:
 * it converts (LibreOffice), validates (veraPDF) and, if needed, remediates (Ghostscript). The Web NEVER
 * converts, validates PDF/A, builds the manifest, computes hashes, nor re-derives validity from a PDF —
 * it only DISPLAYS the authoritative server state and offers gated actions (start / retry / download).
 */
export function PdfaPipelineSection({
  dossierId,
  document,
}: {
  dossierId: string;
  document: DocumentDto;
}): JSX.Element {
  const api = useApi();
  const status = usePipelineStatus(document.id);
  const start = useStartPipeline(dossierId, document.id);
  const retry = useRetryPipeline(dossierId, document.id);

  const [downloadingKind, setDownloadingKind] = useState<ArtifactKind | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const data = status.data;
  const run = data?.run ?? null;
  const pdfaStatus = data?.pdfaStatus ?? document.pdfaStatus;
  const documentStatus = data?.documentStatus ?? document.status;

  const active = run !== null && !TERMINAL.has(run.status);
  const lastFailed = run !== null && FAILED.has(run.status);
  const canStart = documentStatus === 'DOCX_GENERATED' && !active && run === null;

  const runDownload = async (kind: ArtifactKind): Promise<void> => {
    setDownloadError(null);
    setDownloadingKind(kind);
    try {
      await api.downloadArtifact(document.id, kind);
    } catch (err) {
      setDownloadError(toDisplayError(err).message);
    } finally {
      setDownloadingKind(null);
    }
  };

  const transitions = run?.transitions ?? [];
  const lastIndex = transitions.length - 1;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Chaîne PDF/A (archivage)</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={pdfaTone(pdfaStatus)}>{PDFA_STATUS_LABELS[pdfaStatus]}</Badge>
            <Badge tone="neutral">{DOCUMENT_STATUS_LABELS[documentStatus]}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Server authority — the browser never produces or validates the PDF/A. */}
        <p className="text-xs text-slate-500">
          Le PDF/A est produit et validé par le SERVEUR (LibreOffice → veraPDF → Ghostscript),
          jamais par le navigateur. Le Web n’affiche que l’état renvoyé par l’API et propose des
          actions.
        </p>

        {/* Actions — start / retry (server-gated). */}
        <div className="flex flex-wrap items-center gap-2">
          {lastFailed ? (
            <Button
              type="button"
              onClick={() => retry.mutate()}
              disabled={active || retry.isPending}
            >
              {retry.isPending ? 'Relance…' : 'Relancer'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => start.mutate()}
              disabled={!canStart || start.isPending}
            >
              {start.isPending ? 'Lancement…' : 'Lancer la chaîne PDF/A'}
            </Button>
          )}
          {active ? (
            <span className="text-sm text-slate-500">
              Pipeline en cours ({run ? PIPELINE_STATUS_LABELS[run.status] : ''})…
            </span>
          ) : null}
          {status.isLoading ? (
            <span className="text-sm text-slate-500">Chargement de l’état…</span>
          ) : null}
        </div>

        {start.isError ? (
          <Alert variant="error" details={toDisplayError(start.error).details}>
            {toDisplayError(start.error).message}
          </Alert>
        ) : null}
        {retry.isError ? (
          <Alert variant="error" details={toDisplayError(retry.error).details}>
            {toDisplayError(retry.error).message}
          </Alert>
        ) : null}

        {run === null ? (
          <p className="text-sm text-slate-500">
            Aucun pipeline PDF/A pour ce document. Lancez la chaîne une fois le DOCX généré.
          </p>
        ) : (
          <>
            {/* Run summary: attempt count + Ghostscript remediation note. */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={pipelineTone(run.status)}>{PIPELINE_STATUS_LABELS[run.status]}</Badge>
              <span className="text-slate-600">Tentative n° {run.attempt}</span>
            </div>

            {run.ghostscriptUsed ? (
              <Alert variant="warning" title="Remédiation Ghostscript utilisée">
                Le serveur a appliqué une remédiation Ghostscript avant la revalidation veraPDF.
              </Alert>
            ) : null}

            {/* Business error (server message). */}
            {run.error ? <Alert variant="error">{run.error}</Alert> : null}

            {/* Timeline of transitions (ordered) with the current step highlighted. */}
            {transitions.length > 0 ? (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Déroulé du pipeline
                </p>
                <ol className="space-y-2">
                  {transitions.map((t, i) => (
                    <TimelineStep
                      key={`${t.toStatus}-${t.createdAt}-${i}`}
                      transition={t}
                      current={i === lastIndex}
                    />
                  ))}
                </ol>
              </div>
            ) : null}

            {/* veraPDF validation result(s) — the SOLE conformance authority. */}
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">
                Résultat veraPDF (seule autorité de conformité)
              </p>
              {run.validations.length > 0 ? (
                <ul className="space-y-2">
                  {run.validations.map((v, i) => (
                    <ValidationRow key={`${v.veraPdfVersion}-${v.createdAt}-${i}`} validation={v} />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">Aucune validation veraPDF pour l’instant.</p>
              )}
            </div>
          </>
        )}

        {/* Available artifacts (server-produced). */}
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => runDownload('DOCX')}
            disabled={downloadingKind !== null}
          >
            {downloadingKind === 'DOCX' ? 'Téléchargement…' : 'Télécharger DOCX'}
          </Button>
          {pdfaStatus !== 'NOT_GENERATED' ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => runDownload('PDFA')}
              disabled={downloadingKind !== null}
            >
              {downloadingKind === 'PDFA' ? 'Téléchargement…' : 'Télécharger PDF/A'}
            </Button>
          ) : null}
          {run?.ghostscriptUsed ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => runDownload('PDFA_REMEDIATED')}
              disabled={downloadingKind !== null}
            >
              {downloadingKind === 'PDFA_REMEDIATED'
                ? 'Téléchargement…'
                : 'Télécharger PDF/A remédié'}
            </Button>
          ) : null}
          {run && run.validations.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => runDownload('VERAPDF_REPORT')}
              disabled={downloadingKind !== null}
            >
              {downloadingKind === 'VERAPDF_REPORT'
                ? 'Téléchargement…'
                : 'Télécharger rapport veraPDF'}
            </Button>
          ) : null}
        </div>

        {downloadError ? <Alert variant="error">{downloadError}</Alert> : null}

        {status.isError ? (
          <Alert variant="error">{toDisplayError(status.error).message}</Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
